import { num } from './seoMetrics';

/**
 * O score de SEO de um conjunto de páginas.
 *
 * ## Por que este arquivo existe
 *
 * O painel mostrava um score próprio, calculado por uma lista de checagens por
 * página e depois tirada a média. O relatório mostrava outro, vindo de
 * `runs.seo_score`. Os dois se chamavam "Score de SEO", e na execução #33 do
 * mesmo projeto um dizia 89% e o outro dizia 91. Quem abrisse as duas telas não
 * tinha como saber qual acreditar, e quem entregasse o relatório ao cliente
 * estava entregando um número que a própria ferramenta contradizia na tela ao
 * lado.
 *
 * A fórmula que fica é a do relatório: é a que está documentada na página de
 * documentação, no post do blog e no comentário de `services/seoScore.js` no
 * backend, e é a que se explica pelas cinco abas do painel.
 *
 * ## A do painel não foi perdida por descuido
 *
 * Ela somava 15 pontos por ter título, 10 por estar na faixa, 15 por descrição,
 * e assim por diante — **incluindo 5 pontos por `meta_keywords`**, que os
 * buscadores ignoram desde 2009. Era possível ganhar nota preenchendo um campo
 * que ninguém lê.
 *
 * ## Espelho, não cópia livre
 *
 * `backend/services/seoScore.js` é a fonte de verdade: é lá que o número é
 * calculado e gravado ao fim de cada execução. Aqui a mesma regra é aplicada
 * sobre as páginas já carregadas, o que permite ao painel responder ao filtro —
 * ver o score de um recorte é justamente o que a tela oferece e o relatório
 * não.
 *
 * As duas metades são amarradas por `scripts/verificarScore.js` aqui e por um
 * teste de mesmo nome no backend, que usam os mesmos casos. Mudar um lado sem
 * o outro reprova em um dos dois.
 */

export const PESOS = { indexacao: 25, conteudo: 30, experiencia: 20, links: 15, midia: 10 };

export const CARGA_LENTA_MS = 3000;

/** `internal_links` é TEXT[] no banco, mas pode chegar já contado. */
const quantos = (valor) => (Array.isArray(valor) ? valor.length : num(valor));

/**
 * Quantas páginas têm **pelo menos um** problema em cada área.
 *
 * Não é a mesma conta de `problemasPorSecao`, que soma ocorrências para o
 * contador das abas — lá "23" quer dizer vinte e três problemas de conteúdo, e
 * uma página sem título, sem descrição e sem H1 entra três vezes. Aqui ela
 * entra uma: é uma página para abrir e um deploy para publicar, não três.
 *
 * Os predicados espelham o `FILTER` de `SQL_CONTAGENS` no backend, um a um.
 */
export const contagensPorArea = (paginas = []) => {
  const conta = (teste) => paginas.filter(teste).length;

  return {
    total: paginas.length,
    indexacao: conta(
      (p) =>
        !(p.status_code >= 200 && p.status_code < 300) ||
        p.render_failed === true ||
        p.has_canonical_tag !== true
    ),
    conteudo: conta(
      (p) =>
        !p.title ||
        p.title.trim() === '' ||
        !p.meta_description ||
        p.meta_description.trim() === '' ||
        (p.h1_tags || []).length === 0
    ),
    experiencia: conta((p) => num(p.load_time_ms) > CARGA_LENTA_MS || p.has_viewport_meta !== true),
    links: conta((p) => num(p.broken_links_count) > 0 || quantos(p.internal_links) === 0),
    midia: conta((p) => num(p.images_without_alt_count) > 0)
  };
};

/**
 * `100 − Σ(peso × afetadas / total)`.
 *
 * Devolve `null` sem página nenhuma, e não zero: zero é uma medição, e "não há
 * o que medir" não é. A tela decide como mostrar a ausência.
 */
export const calcularScore = (contagens) => {
  const total = Number(contagens?.total) || 0;
  if (total === 0) return null;

  const perda = Object.entries(PESOS).reduce((soma, [area, peso]) => {
    const afetadas = Math.min(total, Number(contagens[area]) || 0);
    return soma + peso * (afetadas / total);
  }, 0);

  return Math.max(0, Math.min(100, Math.round(100 - perda)));
};

/** Atalho: das páginas direto ao número. */
export const scoreDasPaginas = (paginas) => calcularScore(contagensPorArea(paginas));
